import React from 'react'
import classNames from 'classnames'
import { ProductTypeProps } from '.'
import { useResponsive, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import { DigitalDataOrigin } from '../utils/digital-data-types'
import { getProductUrl } from '@dustin-web/microsite-components/src/utils/urls'
import Button from '@dustin-web/microsite-components/src/button'
import { TicketLink } from '../ticket-link'
import { Card } from './product-card-gallery-composable'
import { handeOnClickProduct } from './utils'
import LineClamp from '@dustin-web/microsite-components/src/line-clamp/line-clamp'

type PromotedProduct = {
  imageUrl: string
  campaignTitle: string
}

type Props = {
  product: ProductTypeProps & Partial<PromotedProduct>
  ticket: string
  slim?: boolean
  withBorder?: boolean
  imagePriority?: boolean
  isExtraItem?: boolean
  inFilterList?: boolean
  sizeClasses?: string
  mainProductErpIdentifier?: string
  digitalDataOrigin: DigitalDataOrigin
  digitalIndex?: number
  isProductAccessories?: boolean
  showFavoritesButton?: boolean
  primary?: boolean
  showActionRow?: boolean
  viewType?: string
}

type ProductTitleProps = {
  product: ProductTypeProps & Partial<PromotedProduct>
  ticket: string
  digitalIndex: number
  digitalDataOrigin: DigitalDataOrigin
}

const ProductTitle = ({ product, ticket, digitalIndex, digitalDataOrigin }: ProductTitleProps) => {
  const onClickProduct = () => {
    handeOnClickProduct(digitalDataOrigin, product as any, digitalIndex)
  }

  return (
    <TicketLink
      url={getProductUrl(product.id, product.nameSlug)}
      ticket={ticket}
      className="h2 u-mb-8 u-fs-24"
      onClickCallBack={onClickProduct}>
      <LineClamp text={`${product.displayName}`} />
    </TicketLink>
  )
}

const ProductCardCampaign = ({
  product,
  ticket,
  isExtraItem = false,
  inFilterList = false,
  sizeClasses = '',
  digitalDataOrigin,
  digitalIndex = 0,
  viewType,
}: Props) => {
  const { breakpoints } = useResponsive()
  const { translate } = useTranslations()

  const imageWidth = breakpoints.min.LG ? 240 : breakpoints.min.SM ? 300 : 480
  const imageHeight = breakpoints.min.LG ? 320 : breakpoints.min.SM ? 340 : 320

  const imageOverrideWidth = viewType && breakpoints.max.SM ? 126 : imageWidth
  const imageOverrideWidthHeight = viewType && breakpoints.max.SM ? 140 : imageHeight

  const containerClass = classNames(sizeClasses, 'u-mb-8 u-mb-sm-16 u-color-black', {
    'c-filter__extra-gallery-box': isExtraItem,
    'col-12 col-sm-6 col-md-4': inFilterList && !sizeClasses.length,
    'col-12 col-sm-6 col-md-4 col-lg-4': !inFilterList && !sizeClasses.length,
  })

  if (viewType === 'gallery') {
    return (
      <Card.Container
        digitalDataOrigin={digitalDataOrigin}
        digitalIndex={digitalIndex}
        product={product}
        customClassName={containerClass}
        isCampaignProduct>
        <Card.Wrapper cardClasses="t-campaign u-px-16 u-py-16 flex-column" withTheme>
          <Card.Center>
            <Card.Badge
              isAvailableForSale={product.availability?.isAvailableForSale}
              isNewProduct={false}
              isCampaignProduct={true}
              description={product.campaignTitle?.toUpperCase()}
            />
            <span
              className="c-product-card-title__manufacturer-name u-pt-24 u-fs-16"
              data-test-id="product-card-manufacturerName">
              {product.manufacturerName}
            </span>
            <ProductTitle
              product={product}
              ticket={ticket}
              digitalIndex={digitalIndex}
              digitalDataOrigin={digitalDataOrigin}
            />
          </Card.Center>
          <Card.Content>
            <Card.Top className="d-flex">
              <Card.Picture
                product={product}
                ticket={ticket}
                digitalDataIndex={digitalIndex}
                digitalDataOrigin={digitalDataOrigin}
                className="u-flex-1"
                imageWidth={imageWidth}
                imageHeight={imageHeight}
                productImageUrlOverride={product.imageUrl}
              />
            </Card.Top>
            <Card.Center>
              <Card.Price
                isAvailableForSale={product.availability?.isAvailableForSale}
                price={product.price}
              />
              <div className="c-product-card__add-to-cart-wrapper d-flex">
                <Button
                  onClick={() => {
                    handeOnClickProduct(digitalDataOrigin, product as any, digitalIndex)
                  }}
                  text={translate('GoToProduct')}
                  icon="arrow-right"
                  link
                  href={getProductUrl(product.productErpIdentifier, product.nameSlug)}
                  className="u-mb-0 d-flex u-flex-1"
                />
              </div>
            </Card.Center>
          </Card.Content>
        </Card.Wrapper>
      </Card.Container>
    )
  } else {
    return (
      <Card.Container
        digitalDataOrigin={digitalDataOrigin}
        digitalIndex={digitalIndex}
        product={product}
        customClassName={containerClass}
        isCampaignProduct>
        <Card.Wrapper cardClasses="t-campaign u-px-16 u-py-16 flex-row" withTheme>
          <Card.Center>
            <Card.Picture
              product={product}
              ticket={ticket}
              digitalDataIndex={digitalIndex}
              digitalDataOrigin={digitalDataOrigin}
              className="u-flex-1"
              imageWidth={viewType ? imageOverrideWidth : imageWidth}
              imageHeight={viewType ? imageOverrideWidthHeight : imageHeight}
              productImageUrlOverride={product.imageUrl}
            />
          </Card.Center>
          <Card.Content>
            <Card.Top className="d-flex flex-column">
              <Card.Badge
                isAvailableForSale={product.availability?.isAvailableForSale}
                isNewProduct={false}
                isCampaignProduct={true}
                description={product.campaignTitle?.toUpperCase()}
              />
              <span
                className="c-product-card-title__manufacturer-name u-pt-24 u-fs-16"
                data-test-id="product-card-manufacturerName">
                {product.manufacturerName}
              </span>
              <ProductTitle
                product={product}
                ticket={ticket}
                digitalIndex={digitalIndex}
                digitalDataOrigin={digitalDataOrigin}
              />
            </Card.Top>
            <Card.Center>
              <Card.Price
                isAvailableForSale={product.availability?.isAvailableForSale}
                price={product.price}
              />
            </Card.Center>
          </Card.Content>
        </Card.Wrapper>
      </Card.Container>
    )
  }
}

export default ProductCardCampaign
